import { z } from 'zod';
import { reportCubeQuerySchema, reportExportFormatSchema } from './query-schema.js';
import { reportScheduleRuntimeFilterSchema, } from './runtime-filters.js';
/**
 * Supported schedule frequencies
 */
export const scheduleFrequencySchema = z.enum([
    'daily',
    'weekly',
    'monthly',
    'quarterly',
    'yearly',
]);
/**
 * Days of the week (0 = Sunday, 6 = Saturday)
 */
const dayOfWeekSchema = z.number().int().min(0).max(6);
/**
 * Ordinal position for monthly schedules (e.g., "first Monday")
 */
export const ordinalPositionSchema = z.enum(['first', 'second', 'third', 'fourth', 'last']);
/**
 * Quarterly timing options relative to calendar quarters (Jan-Mar, Apr-Jun, Jul-Sep, Oct-Dec)
 */
export const quarterlyTimingSchema = z.enum(['startOfQuarter', 'midQuarter', 'endOfQuarter']);
/**
 * Configuration details that vary by frequency.
 *
 * - daily: no additional config needed
 * - weekly: daysOfWeek (which days to run)
 * - monthly: either dayOfMonth or ordinal + dayOfWeek
 * - yearly: month + dayOfMonth
 */
export const scheduleConfigSchema = z.union([
    z.object({
        frequency: z.literal('daily'),
    }),
    z.object({
        frequency: z.literal('weekly'),
        daysOfWeek: z.array(dayOfWeekSchema).min(1, 'Select at least one day'),
    }),
    z.object({
        frequency: z.literal('monthly'),
        monthlyType: z.literal('dayOfMonth'),
        dayOfMonth: z.number().int().min(1).max(31),
    }),
    z.object({
        frequency: z.literal('monthly'),
        monthlyType: z.literal('lastDay'),
    }),
    z.object({
        frequency: z.literal('monthly'),
        monthlyType: z.literal('ordinal'),
        ordinalPosition: ordinalPositionSchema,
        dayOfWeek: dayOfWeekSchema,
    }),
    z.object({
        frequency: z.literal('quarterly'),
        quarterlyTiming: quarterlyTimingSchema,
    }),
    z.object({
        frequency: z.literal('yearly'),
        month: z.number().int().min(1).max(12),
        dayOfMonth: z.number().int().min(1).max(31),
    }),
]);
/**
 * Schema for creating a new report schedule
 */
export const reportScheduleCreateSchema = z.object({
    endDate: z.coerce.date().optional().nullable(),
    format: reportExportFormatSchema,
    name: z.string().min(1, 'Name is required').max(255),
    recipientIds: z.array(z.number().int()).min(1, 'Select at least one recipient'),
    reportId: z.number().int(),
    runtimeFilterValues: z.array(reportScheduleRuntimeFilterSchema).optional(),
    scheduleConfig: scheduleConfigSchema,
    startDate: z.coerce.date(),
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be in HH:mm format (00:00–23:59)'),
});
/**
 * Schema for updating an existing report schedule
 */
export const reportScheduleUpdateSchema = reportScheduleCreateSchema
    .omit({ reportId: true })
    .partial()
    .extend({ isActive: z.boolean().optional() });
/**
 * Schema for ad-hoc report email (send now, no schedule)
 */
export const reportEmailAdhocSchema = z.object({
    format: reportExportFormatSchema,
    groupByMembers: z.array(z.string()).default([]),
    query: reportCubeQuerySchema,
    recipientIds: z.array(z.number().int()).min(1, 'Select at least one recipient'),
    reportName: z.string().default('Report'),
    selectedMembers: z.array(z.string()).min(1, 'At least one member must be selected'),
});
/**
 * Generates a human-readable label for a schedule configuration
 *
 * @param config - The schedule configuration
 * @param time - Time string in HH:mm format
 * @returns Human-readable schedule description
 */
export function formatScheduleLabel(config, time) {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const monthNames = [
        '',
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];
    const formatTime = (t) => {
        const [h, m] = t.split(':').map(Number);
        const period = h >= 12 ? 'PM' : 'AM';
        const hour12 = h % 12 || 12;
        return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
    };
    const timeStr = formatTime(time);
    switch (config.frequency) {
        case 'daily':
            return `Daily at ${timeStr}`;
        case 'weekly': {
            const days = config.daysOfWeek.map((d) => dayNames[d]).join(', ');
            return `Weekly on ${days} at ${timeStr}`;
        }
        case 'monthly': {
            if (config.monthlyType === 'dayOfMonth') {
                return `Monthly on the ${ordinalSuffix(config.dayOfMonth)} at ${timeStr}`;
            }
            if (config.monthlyType === 'lastDay') {
                return `Monthly on the last day at ${timeStr}`;
            }
            const pos = config.ordinalPosition.charAt(0).toUpperCase() + config.ordinalPosition.slice(1);
            return `Monthly on the ${pos} ${dayNames[config.dayOfWeek]} at ${timeStr}`;
        }
        case 'quarterly': {
            const timingLabels = {
                startOfQuarter: 'start',
                midQuarter: 'middle',
                endOfQuarter: 'end',
            };
            return `Quarterly at the ${timingLabels[config.quarterlyTiming]} of each quarter at ${timeStr}`;
        }
        case 'yearly':
            return `Yearly on ${monthNames[config.month]} ${config.dayOfMonth} at ${timeStr}`;
    }
}
/**
 * Appends the correct ordinal suffix to a number (1st, 2nd, 3rd, etc.)
 */
function ordinalSuffix(n) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}
